import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { Close } from 'app/components/Common/Close'
import { Props as MenuItemProps } from 'app/components/Common/Header/MenuItem'
import { FlashNews, Props as FlashNewsProps } from 'app/components/FlashNews'
import { Gift, Logo as LogoIcon, LogoSmall } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { HeaderContext } from 'app/containers/Header'
import { theme } from 'app/theme'
import { Media } from 'app/theme/media'
import useLockedBody from 'app/utils/useLockedBody'
import { useVocabularyData } from 'app/utils/vocabulary'
import { Link } from 'gatsby'
import React, { memo, useContext, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { Hamburger } from './Hamburger'
import { Props as LanguageItemProps } from './LanguageItem'
import { Languages } from './Languages'
import { Navigation } from './Navigation'
import { Syncrobox } from './Syncrobox'

export interface Props {
  flashNewsProps?: FlashNewsProps
  languagesList: LanguageItemProps[]
  menuItems: MenuItemProps[]
  pageID?: string
  variant?: Variant
}

export const Header = memo(function Header({
  flashNewsProps,
  languagesList,
  menuItems,
}: Props) {
  const { contactsPageURL, languageCode, languagePrefix, siteName, variant } =
    useContext(HeaderContext) as any

  let beLanguage = ''
  switch (languageCode) {
    case 'en-US':
      beLanguage = 'eng'
      break

    default:
      beLanguage = 'ita'
      break
  }

  const [_locked, setLocked] = useLockedBody()
  const [modalStatus, setModalStatus] = useState(false)
  const [modalIBEStatus, setModalIBEStatus] = useState(false)
  const [startAnimation, setStartAnimation] = useState(false)
  const [scroll, setScroll] = useState(false)

  const [animObjectRef, isAnimObjectVisible] = useInView({
    triggerOnce: true,
  })

  const contactsRealPageURL = languagePrefix
    ? `/${languagePrefix}/${contactsPageURL}`
    : `/${contactsPageURL}`

  useEffect(() => {
    const onScroll = () => {
      setScroll(window.scrollY > 120)
    }

    window.addEventListener('scroll', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  return (
    <Container>
      {flashNewsProps ? <FlashNews {...flashNewsProps} /> : null}

      <Head
        className={`${scroll ? 'fixed' : ''}${
          variant === 'compact' ? ' compact' : ''
        }${isAnimObjectVisible ? ' visible' : ''}`}
        dial={4}
        ref={animObjectRef}
        row
      >
        <Hamburger
          isSticky={scroll || variant === 'compact'}
          onClick={() => {
            if (!modalStatus) {
              setLocked(true)
              setModalStatus(true)
            } else {
              setLocked(false)
              setModalStatus(false)
            }

            setTimeout(() => {
              setStartAnimation(true)
            }, 600)
          }}
        />

        <Media
          greaterThanOrEqual="desktopSmall"
          style={{ marginRight: 'auto' }}
        >
          <Languages
            isSticky={scroll || variant === 'compact'}
            languagesList={languagesList}
            languagePrefix={languagePrefix}
          />
        </Media>

        <Logo
          aria-label={siteName}
          to={languagePrefix ? `/${languagePrefix}` : '/'}
        >
          <Media lessThan="desktopSmall">
            <LogoSmall />
          </Media>

          <Media greaterThanOrEqual="desktopSmall">
            {scroll || variant === 'compact' ? <LogoSmall /> : <LogoIcon />}
          </Media>
        </Logo>

        <Media lessThan="desktopSmall">
          <ShopCTAMobile
            target="_blank"
            href={`https://reservations.verticalbooking.com//sp_prenota_voucher_trattamenti.htm?id_stile=21249&id_albergo=28023&dc=8110&lingua_int=${beLanguage}`}
          >
            <Gift />
            {useVocabularyData('shop-label', languageCode)}
          </ShopCTAMobile>
        </Media>

        <Media greaterThanOrEqual="desktopSmall">
          <ShopCTA
            label={useVocabularyData('shop-label', languageCode)}
            target="_blank"
            URL={`https://reservations.verticalbooking.com//sp_prenota_voucher_trattamenti.htm?id_stile=21249&id_albergo=28023&dc=8110&lingua_int=${beLanguage}`}
            variant={scroll || variant === 'compact' ? 'invert' : 'light'}
          />
          <BookingCTA
            label={useVocabularyData('book', languageCode)}
            URL={`https://reservations.verticalbooking.com/premium/index.html?id_albergo=20080&dc=4328&id_stile=15720&lingua_int=${beLanguage}`}
            rel="noopener"
            target="_blank"
            variant="cta"
          />
        </Media>
      </Head>
      <Media lessThan="desktopSmall">
        <Toolbar>
          <BEDirectLink
            href={`https://reservations.verticalbooking.com/premium/index.html?id_albergo=20080&dc=4328&id_stile=15720&lingua_int=${beLanguage}`}
            rel="noopener"
            target="_blank"
          >
            {useVocabularyData('book', languageCode)}
          </BEDirectLink>
        </Toolbar>
      </Media>

      <Modal className={modalStatus ? 'open' : undefined}>
        <Close
          className={modalStatus ? 'open' : undefined}
          onClick={() => {
            setLocked(false)
            setModalStatus(false)
            setStartAnimation(false)
          }}
        />

        <Media lessThan="desktopSmall">
          <Languages
            languagesList={languagesList}
            languagePrefix={languagePrefix}
          />
        </Media>

        <Navigation
          languagePrefix={languagePrefix}
          menuItems={menuItems}
          modalStatus={modalStatus}
          siteName={siteName}
          startAnimation={startAnimation}
        />
      </Modal>

      <Media greaterThanOrEqual="desktopSmall">
        <Modal className={modalIBEStatus ? 'open' : undefined}>
          <Close
            onClick={() => {
              setLocked(false)
              setModalIBEStatus(false)
            }}
            position="R"
          />

          <Syncrobox
            beLanguage={beLanguage}
            languageCode={languageCode}
            languagePrefix={languagePrefix || 'en'}
            modalStatus={modalIBEStatus}
            siteName={siteName}
          />
        </Modal>
      </Media>
    </Container>
  )
})

const Container = styled.section`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
`

const Logo = styled(Link)`
  opacity: 0;
  position: absolute;
  top: 2.25rem;
  left: 50%;
  transform: translate(-50%, 2.5rem);
  transition:
    opacity 1s 1.9s ease-in-out,
    transform 1s 1.9s ease-in-out;

  svg {
    width: 11.8125rem;
    height: 5.625rem;
    fill: ${({ theme }) => theme.colors.variants.neutralLight5};
  }
`

const ShopCTA = styled(Button)`
  opacity: 0;
  margin-right: 0.25rem;
  font-size: 0.8125rem;
  padding: 1.25rem 2.25rem 1.25rem 2.25rem;
  transform: translateY(2.5rem);
  transition:
    opacity 1s 2.1s ease-in-out,
    transform 1s 2.1s ease-in-out;
`

const BookingCTA = styled(Button)`
  opacity: 0;
  transform: translateY(2.5rem);
  font-size: 0.8125rem;
  transition:
    opacity 1s 2.2s ease-in-out,
    transform 1s 2.2s ease-in-out;
`

const HeaderFixedStyle = css`
  background: ${theme.colors.variants.neutralLight5};
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;

  ${Logo} {
    top: 1.25rem;
    opacity: 1 !important;
    transition: none !important;
    svg {
      width: 7.6875rem;
      height: 2rem;
      fill: ${theme.colors.variants.primaryDark};
    }
  }

  ${ShopCTA} {
    opacity: 1 !important;
    transition: none !important;
  }

  ${BookingCTA} {
    opacity: 1 !important;
    transition: background 0.3s ease-in-out !important;

    &:hover {
      color: ${theme.colors.variants.primaryDark};
    }
  }

  @media (max-width: 1199px) {
    height: 3.75rem;

    ${Logo} {
      opacity: 1 !important;
      transform: translateX(-50%) !important;
      transition: none !important;
      top: 0.875rem;
    }
  }
`

const Head = styled(FlexBox)`
  width: 100%;
  padding-right: 0.5rem;
  position: relative;
  top: 0;
  &.fixed {
    animation: fadeInTop 0.3s;

    ${HeaderFixedStyle}
  }
  &.compact {
    animation: none !important;

    ${HeaderFixedStyle}
  }
  &.visible {
    ${Logo} {
      opacity: 1;
      transform: translateX(-50%);
    }
    ${ShopCTA},
    ${BookingCTA} {
      opacity: 1;
      transform: none;
    }
  }

  @keyframes fadeInTop {
    0% {
      top: -4.875rem;
    }
    100% {
      top: 0;
    }
  }

  @media (max-width: 1199px) {
    animation: none !important;

    ${HeaderFixedStyle}
  }
`

const ShopCTAMobile = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 3.75rem;
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.75rem;
  font-weight: 300;
  padding: 0 0.75rem;
  position: absolute;
  top: 0;
  right: 0;
  border-left: 1px solid ${({ theme }) => theme.colors.variants.neutralLight3};

  svg {
    height: 1rem;
    fill: ${({ theme }) => theme.colors.variants.primaryDark};
    margin-bottom: 0.375rem;
  }

  @media (max-width: 375px) {
    padding: 0 0.5625rem;
  }
`

const Toolbar = styled(FlexBox)`
  width: 100%;
  height: 3.75rem;
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 1;
`

const BEDirectLink = styled.a`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 3.75rem;
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  background: ${({ theme }) => theme.colors.variants.primaryLight};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;

  @media (max-width: 375px) {
    padding: 0 0.5625rem;
  }
`

const Modal = styled.div`
  width: 100%;
  height: 100%;
  opacity: 0;
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  z-index: -1;
  transition: 0.2s ease-out;
  &.open {
    opacity: 1;
    visibility: visible;
    z-index: 10000;
  }
`

export type Variant = 'default' | 'compact'
